import { ref, computed } from 'vue';

// Feature keys (PR-870), loaded once per session from GET /api/Configuration/Features and read
// through featureFlags.ts. Fails closed: if the fetch fails every key reads as disabled, logged
// rather than toasted because bootstrap runs before any page can mount a toast host.
// See docs/technical-requirements/feature-flags.md.

/**
 * A string-literal union on purpose: deleting a released key turns every leftover guard into a
 * compile error rather than a silently-false condition.
 */
export type FeatureKey = 'assays' | 'measurements' | 'fsr';

const enabled = ref<Record<string, boolean>>({});
const loaded = ref(false);

export const FeatureService = {
  /** Fetch the key map once per session. Never throws; on failure every key reads as disabled. */
  async load(): Promise<void> {
    try {
      // Lazy import, matching MaintenanceIntervalService: a static one would pull axiosInstance —
      // and so `window` — into module load and break every node-environment spec.
      const { default: configurationAPI } = await import('@/apiServices/configurationAPI');
      const response = await configurationAPI.getFeatures();
      enabled.value = response.data ?? {};
      loaded.value = true;
    } catch (error) {
      console.error('FeatureService.load failed; every feature key will read as disabled', error);
      enabled.value = {};
      loaded.value = false;
    }
  },

  /** True only when the environment explicitly enabled the key. Anything else is disabled (FR-7). */
  isEnabled(key: FeatureKey): boolean {
    return enabled.value[key] === true;
  },

  /** True once a map has been fetched. Callers rarely need this — a failed load reads as all-disabled. */
  isLoaded: computed(() => loaded.value),

  /** Drop the cache on logout, so a second user in the same tab re-fetches rather than inherits. */
  reset(): void {
    enabled.value = {};
    loaded.value = false;
  },
};

export default FeatureService;
